import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useStores } from '@/hooks';
import { AppTag } from '@pro-sto/ui';
import { formatPrice, resolveUploadUrl } from '@pro-sto/shared/utils';
import { formatPhoneWithMask, formatClientName } from '@/features/clients';
import type { AdminBookingCarDto } from '@pro-sto/api-client';
import './ViewBookingModal.css';

// Расширенный тип для автомобиля с дополнительными полями
type BookingCarWithImage = AdminBookingCarDto & {
  generated_image?: string;
};

interface ViewBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingUuid: string;
  onUpdate: () => void;
  onEdit?: () => void;
}

// Маппинг статусов на параметры тегов
const STATUS_CONFIG: Record<string, { color: 'yellow' | 'blue' | 'green' | 'red'; label: string }> = {
  pending_confirmation: { color: 'yellow', label: 'Ожидает' },
  confirmed: { color: 'green', label: 'Подтвержден' },
  completed: { color: 'green', label: 'Завершено' },
  cancelled: { color: 'red', label: 'Отменён' },
  paid: { color: 'blue', label: 'Оплачена' },
};

const ViewBookingModal = observer(({
  isOpen,
  onClose,
  bookingUuid,
  onUpdate,
  onEdit,
}: ViewBookingModalProps) => {
  const { bookingsStore } = useStores();

  useEffect(() => {
    if (isOpen && bookingUuid) {
      bookingsStore.fetchBookingDetails(bookingUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bookingUuid]);

  useEffect(() => {
    // Блокируем скролл при открытии модалки
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCancelBooking = async () => {
    const confirmed = window.confirm('Вы уверены, что хотите отменить эту запись?');
    if (!confirmed) return;

    const success = await bookingsStore.updateBookingStatus(bookingUuid, 'cancelled');
    if (success) {
      onUpdate();
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const booking = bookingsStore.selectedBooking;

  // Состояние загрузки
  if (bookingsStore.isLoadingDetails) {
    return (
      <div className="view-booking-modal">
        <div className="view-booking-modal__skeleton-header">
          <div className="view-booking-modal__skeleton view-booking-modal__skeleton-title" />
          <div className="view-booking-modal__skeleton view-booking-modal__skeleton-tag" />
        </div>
        <div className="view-booking-modal__skeleton-content">
          <div className="view-booking-modal__skeleton view-booking-modal__skeleton-car-section" />
          <div className="view-booking-modal__skeleton view-booking-modal__skeleton-service-section" />
          <div className="view-booking-modal__skeleton-footer">
            <div className="view-booking-modal__skeleton view-booking-modal__skeleton-button" />
            <div className="view-booking-modal__skeleton view-booking-modal__skeleton-button" />
          </div>
        </div>
      </div>
    );
  }

  // Обработка ошибок
  if (!booking && bookingsStore.error) {
    return (
      <div className="view-booking-modal" data-testid="view-booking-modal">
        <div className="view-booking-modal__header">
          <h2 className="view-booking-modal__title">Ошибка</h2>
          <button className="view-booking-modal__close" onClick={handleClose} aria-label="Закрыть" data-testid="view-booking-close-button">
            ×
          </button>
        </div>
        <div className="view-booking-modal__content">
          <p>Не удалось загрузить информацию о бронировании</p>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  // Форматирование даты и времени
  let formattedDate = '';
  let formattedTime = '';
  
  try {
    const startDate = parseISO(booking.start_time);
    const endDate = parseISO(booking.end_time);
    
    // Проверяем, что даты валидны
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error('Invalid dates in booking:', booking);
      formattedDate = 'Неверная дата';
      formattedTime = 'Неверное время';
    } else {
      formattedDate = format(startDate, 'd MMMM', { locale: ru });
      formattedTime = `${format(startDate, 'HH:mm')}-${format(endDate, 'HH:mm')}`;
    }
  } catch (error) {
    console.error('Error formatting dates:', error);
    formattedDate = 'Ошибка форматирования';
    formattedTime = 'Ошибка форматирования';
  }

  // Форматирование госномера - может быть строкой или объектом. booking.car может
  // отсутствовать (null) — например, если клиент удалил машину после создания записи.
  const licensePlateData = booking.car?.license_plate;
  let licensePlateNumber = 'Не указан';
  let licensePlateRegion: string | null = null;

  if (typeof licensePlateData === 'string') {
    // Если license_plate это строка (AdminBookingCarDto)
    licensePlateNumber = licensePlateData || 'Не указан';
  } else if (licensePlateData && typeof licensePlateData === 'object') {
    // Если license_plate это объект (BookingCarDto)
    const plate = licensePlateData as { number?: string; region?: string };
    licensePlateNumber = plate.number || 'Не указан';
    licensePlateRegion = plate.region || null;
  }

  // Конфигурация статуса
  const displayStatus = booking.payment_status === 'paid' ? 'paid' : booking.status;
  const statusConfig = STATUS_CONFIG[displayStatus] || STATUS_CONFIG.pending_confirmation;

  // Форматирование длительности в минутах
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours} ч ${remainingMinutes} мин` : `${hours} ч`;
  };

  // Получаем комментарий как строку (client_comment может быть строкой или null, но типизирован как object | null)
  const clientComment = typeof booking.client_comment === 'string' ? booking.client_comment : null;

  const resolveClientPhone = (): string => {
    const candidates = [
      booking.client?.phone,
      bookingsStore.getBookingFromList(bookingUuid)?.client?.phone,
    ];

    for (const phone of candidates) {
      if (typeof phone === 'string' && phone.replace(/\D/g, '').length >= 10) {
        return phone;
      }
    }

    return '';
  };

  const clientPhoneRaw = resolveClientPhone();
  const clientPhone = clientPhoneRaw
    ? formatPhoneWithMask(clientPhoneRaw.replace(/\D/g, ''))
    : null;

  const resolveClientName = (): string => {
    const candidates = [
      booking.client?.name,
      bookingsStore.getBookingFromList(bookingUuid)?.client?.name,
    ];

    for (const name of candidates) {
      const formatted = formatClientName(name);
      if (formatted) return formatted;
    }

    return '';
  };

  const clientName = resolveClientName();

  // Подсчет итоговой стоимости
  const totalPrice = booking.service.price + 
    (booking.additionalServices?.reduce((sum, service) => sum + service.price, 0) || 0);

  return (
    <div className="view-booking-modal" data-testid="view-booking-modal">
      <div className="view-booking-modal__header">
        <h2 className="view-booking-modal__title">Запись</h2>
        <AppTag size="M" color={statusConfig.color}>
          {statusConfig.label}
        </AppTag>
        <button className="view-booking-modal__close" onClick={handleClose} aria-label="Закрыть" data-testid="view-booking-close-button">
          ×
        </button>
      </div>

      <div className="view-booking-modal__content">
        {/* Блок информации об автомобиле */}
        <div className="view-booking-modal__car-section" data-testid="view-booking-car-section">
          {(() => {
            const car = booking.car as BookingCarWithImage | null;
            return car?.generated_image && car.generated_image.trim() !== '' && (
              <div className="view-booking-modal__car-image">
                <img
                  src={resolveUploadUrl(
                    car.generated_image,
                    import.meta.env.VITE_BASE_STATIC_PATH ?? '',
                  )}
                  alt={`${car.make} ${car.model}`}
                  className="view-booking-modal__car-photo"
                />
              </div>
            );
          })()}
          <div className="view-booking-modal__car-info">
            <div className="view-booking-modal__car-name">
              {booking.car ? `${booking.car.make} ${booking.car.model}` : 'Без автомобиля'}
            </div>
            <div className="view-booking-modal__car-plate-container">
              <span className="view-booking-modal__car-plate-number">{licensePlateNumber}</span>
              {licensePlateRegion && (
                <>
                  <span className="view-booking-modal__car-plate-divider">|</span>
                  <span className="view-booking-modal__car-plate-region">{licensePlateRegion}</span>
                </>
              )}
            </div>
            {clientPhone && (
              <a
                className="view-booking-modal__client-phone"
                href={`tel:${clientPhoneRaw}`}
                data-testid="view-booking-client-phone"
              >
                {clientPhone}
                {clientName && (
                  <span className="view-booking-modal__client-name"> ({clientName})</span>
                )}
              </a>
            )}
          </div>
        </div>

        {/* Блок информации об услуге */}
        <div className="view-booking-modal__service-section" data-testid="view-booking-service-section">
          <div className="view-booking-modal__datetime" data-testid="view-booking-datetime">
            {formattedDate}, {formattedTime}
          </div>

          <div className="view-booking-modal__divider" />

          <div className="view-booking-modal__service-list">
            {/* Основная услуга */}
            <div className="view-booking-modal__service-item">
              <span className="view-booking-modal__service-name">{booking.service.name}</span>
              <div className="view-booking-modal__service-info">
                <span className="view-booking-modal__service-duration">
                  {formatDuration(booking.service.duration_minutes)}
                </span>
                <span className="view-booking-modal__service-price">{formatPrice(booking.service.price)}₽</span>
              </div>
            </div>

            {/* Дополнительные услуги */}
            {booking.additionalServices && booking.additionalServices.length > 0 && (
              <>
                {booking.additionalServices.map((service) => (
                  <div key={service.uuid} className="view-booking-modal__service-item">
                    <span className="view-booking-modal__service-name">{service.name}</span>
                    <div className="view-booking-modal__service-info">
                      <span className="view-booking-modal__service-duration">
                        {formatDuration(service.duration_minutes)}
                      </span>
                      <span className="view-booking-modal__service-price">{formatPrice(service.price)}₽</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="view-booking-modal__divider" />

          {/* Итого */}
          <div className="view-booking-modal__total" data-testid="view-booking-total">
            <span className="view-booking-modal__total-label">Итого</span>
            <span className="view-booking-modal__total-price">{formatPrice(totalPrice)}₽</span>
          </div>
        </div>

        {/* Блок комментария к заказу */}
        {clientComment && (
          <div className="view-booking-modal__comment-section">
            <div className="view-booking-modal__comment-label">Комментарий к заказу</div>
            <div className="view-booking-modal__comment-text">{clientComment}</div>
          </div>
        )}

        {/* Футер с кнопками */}
        {booking.status !== 'cancelled' && (
          <div className="view-booking-modal__footer">
            {onEdit && booking.status !== 'completed' && (
              <button 
                className="view-booking-modal__edit-button"
                onClick={onEdit}
                data-testid="view-booking-edit-button"
              >
                <span>Редактировать запись</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.8333 3.16663L16.8333 5.16663M15.8333 2.16663L10.5 7.49996C10.1464 7.85358 9.96959 8.03039 9.8306 8.23283C9.70824 8.41183 9.61055 8.60737 9.5405 8.81407C9.46168 9.04706 9.42819 9.29584 9.36122 9.79341L9.00001 12.5L11.7066 12.1388C12.2042 12.0718 12.453 12.0383 12.6859 11.9595C12.8926 11.8895 13.0882 11.7918 13.2672 11.6694C13.4696 11.5304 13.6464 11.3536 14 11L19.3333 5.66663C19.6445 5.35541 19.8001 5.1998 19.9066 5.02143C20.1652 4.58429 20.1652 4.04896 19.9066 3.61182C19.8001 3.43346 19.6445 3.27784 19.3333 2.96663C19.0221 2.65541 18.8665 2.4998 18.6881 2.39328C18.251 2.13471 17.7156 2.13471 17.2785 2.39328C17.1001 2.4998 16.9445 2.65541 16.6333 2.96663L11.3 8.29996M4.66668 3.33329H3.50001C2.8537 3.33329 2.53054 3.33329 2.27248 3.39065C1.46738 3.57043 0.837098 4.20072 0.657319 5.00582C0.599976 5.26388 0.599976 5.58703 0.599976 6.23335V16.5C0.599976 17.1463 0.599976 17.4694 0.657319 17.7275C0.837098 18.5326 1.46738 19.1629 2.27248 19.3426C2.53054 19.4 2.8537 19.4 3.50001 19.4H13.7667C14.413 19.4 14.7361 19.4 14.9942 19.3426C15.7993 19.1629 16.4296 18.5326 16.6093 17.7275C16.6667 17.4694 16.6667 17.1463 16.6667 16.5V15.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {booking.status !== 'completed' && (
              <button className="view-booking-modal__cancel-button" onClick={handleCancelBooking} data-testid="view-booking-cancel-button">
                <span>Отменить запись</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewBookingModal;
